import debounce from 'lodash.debounce';
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import ImprovedSelect from './ImprovedSelect';
import CreateLaboratoryModal from '../../Modal/CreateLaboratoryModal';
import LaboratoryLogo from '../../Laboratory/LaboratoryLogo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useFormContext } from 'react-hook-form';
import { API_BASE_URL } from '../../../api';

const PAGE_SIZE = 20;

const InputLaboratory = ({
  name,
  handleChange,
  errors,
  validationRules = {},
  className,
  placeholder = 'Rechercher un laboratoire...',
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [currentKeyword, setCurrentKeyword] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const inputRef = useRef(null);
  const pinnedOptionRef = useRef(null);
  const { control, getValues } = useFormContext();

  const fetchLabs = useCallback(async (keyword, pageNum, replace) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/v1/laboratories`, {
        withCredentials: true,
        params: { page: pageNum, size: PAGE_SIZE, keyword },
      });
      const content = data.content || [];
      const formatted = content.map(l => ({ value: l.id, label: l.name, icon: <LaboratoryLogo id={l.id} name={l.name} size={24} /> }));
      setOptions(prev => {
        const base = replace ? formatted : [...prev, ...formatted];
        const pinned = pinnedOptionRef.current;
        return (pinned && !base.some(o => o.value === pinned.value)) ? [pinned, ...base] : base;
      });
      setHasMore(!data.last);
      setPage(pageNum);
    } catch (e) {
      console.error('Failed to fetch laboratories', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedSearch = useMemo(() => debounce((keyword) => {
    setCurrentKeyword(keyword);
    fetchLabs(keyword, 0, true);
  }, 300), [fetchLabs]);

  useEffect(() => {
    fetchLabs('', 0, true);
    return () => debouncedSearch.cancel();
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchLabs(currentKeyword, page + 1, false);
    }
  }, [loading, hasMore, currentKeyword, page, fetchLabs]);

  useEffect(() => {
    const initializeDefaultValue = async () => {
      const elementId = getValues(name);
      if (elementId && !options.find(o => o.value === elementId)) {
        try {
          const { data } = await axios.get(`${API_BASE_URL}/api/v1/laboratories/${elementId}`, { withCredentials: true });
          if (data && inputRef.current) {
            const opt = { value: data.id, label: data.name, icon: <LaboratoryLogo id={data.id} name={data.name} size={24} /> };
            pinnedOptionRef.current = opt;
            inputRef.current.setExternalValue(opt);
            setOptions(prev => prev.some(o => o.value === opt.value) ? prev : [...prev, opt]);
          }
        } catch {
          // laboratory no longer exists — leave the field empty
        }
      }
    };
    initializeDefaultValue();
  }, [getValues, name]);

  const handleChangeAndPin = useCallback((option) => {
    pinnedOptionRef.current = option || null;
    handleChange?.(option);
  }, [handleChange]);

  const handleLabCreated = (created) => {
    if (!created) return;
    const opt = { value: created.id, label: created.name, icon: <LaboratoryLogo id={created.id} name={created.name} size={24} /> };
    pinnedOptionRef.current = opt;
    setOptions(prev => [opt, ...prev.filter(o => o.value !== opt.value)]);
    inputRef.current?.setExternalValue(opt);
    setShowCreate(false);
  };

  const createFooter = (
    <button
      type="button"
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', padding: '8px 12px',
        background: '#f0fdf4', border: 'none', borderTop: '1px solid #e5e7eb',
        cursor: 'pointer', color: '#15803d', fontSize: 13, fontWeight: 500,
      }}
      onClick={() => setShowCreate(true)}
    >
      <FontAwesomeIcon icon={faPlus} />
      Créer{currentKeyword ? ` "${currentKeyword}"` : ' un nouveau laboratoire'}
    </button>
  );

  return (
    <div className="position-relative">
      <ImprovedSelect
        ref={inputRef}
        name={name}
        multiple={false}
        options={options}
        onChange={handleChangeAndPin}
        onInputChange={debouncedSearch}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        control={control}
        errors={errors}
        validationRules={validationRules}
        loading={loading}
        className={className}
        noOptionsFooter={createFooter}
        placeholder={placeholder}
      />
      {showCreate && (
        <CreateLaboratoryModal
          show={showCreate}
          onClose={() => setShowCreate(false)}
          laboratory={currentKeyword ? { name: currentKeyword } : null}
          onSaved={handleLabCreated}
        />
      )}
    </div>
  );
};

export default InputLaboratory;
